import { Link } from "react-router-dom";
import {
  ShieldCheck, Lock, FileText, Database, Bot, Mail, Globe,
  ArrowUpRight, Download, Eye, UserX, Clock,
} from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";

const PROCESSORS = [
  {
    name: "Fireflies.ai",
    purpose: "AI Strategist voice agent + call transcription",
    data: "Voice recording, transcript, contact details you choose to share",
    region: "United States · GDPR + SOC 2 Type II",
    link: "https://fireflies.ai/privacy",
  },
  {
    name: "SendGrid (Twilio)",
    purpose: "Transactional email delivery (booking confirmations, follow-ups)",
    data: "Email address, name, message content you send via our forms",
    region: "United States · GDPR + ISO 27001",
    link: "https://www.twilio.com/en-us/legal/privacy",
  },
  {
    name: "MongoDB Atlas",
    purpose: "Encrypted storage of bookings, contact messages, scorecard submissions",
    data: "Form submissions, transcripts, attribution baselines",
    region: "Australia (Sydney region) · ISO 27001 + SOC 2",
    link: "https://www.mongodb.com/legal/privacy-policy",
  },
  {
    name: "Emergent Cloud",
    purpose: "Application hosting + deployment",
    data: "Anonymised page analytics (PostHog) · IP for security",
    region: "Australia/US · SOC 2 Type II",
    link: "https://www.emergent.sh/privacy",
  },
];

const PRINCIPLES = [
  {
    icon: Eye,
    title: "Minimum data",
    body: "We only collect what we need to do the work. No tracking pixels beyond anonymised analytics. No data brokers. No re-selling of contacts.",
  },
  {
    icon: Lock,
    title: "Encrypted at rest and in transit",
    body: "TLS 1.3 in transit. AES-256 at rest. Database access restricted by IP allowlist and role-based credentials.",
  },
  {
    icon: Clock,
    title: "Bounded retention",
    body: "Booking and contact records retained 7 years for tax / audit obligations. AI call transcripts retained 24 months, then permanently deleted. You can request deletion sooner — see below.",
  },
  {
    icon: UserX,
    title: "Your right to walk away",
    body: "You can request export or deletion of everything we hold about you at any time, no questions asked. We action within 30 days, usually within 5 business days.",
  },
];

export default function Trust() {
  return (
    <>
      <section className="container-x pt-24 md:pt-32 pb-12" data-testid="trust-hero">
        <div className="overline">Trust, privacy & data</div>
        <h1 className="mt-6 max-w-3xl font-display font-light tracking-tight text-balance text-5xl sm:text-6xl md:text-7xl leading-[1.04]">
          Your data <span className="text-gold">stays yours</span>.
        </h1>
        <p className="mt-8 max-w-2xl text-zinc-300 leading-relaxed text-lg">
          Toulios Advisory Group handles your data under the Australian
          Privacy Principles (APPs). This page is the plain-English summary.
          The formal documents sit below for record-keeping.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Link to="/privacy" className="btn-outline-white" data-testid="trust-cta-privacy">
            Full Privacy Policy <ArrowUpRight className="w-4 h-4" />
          </Link>
          <a
            href="/privacy-policy.pdf"
            download
            className="btn-ghost"
            data-testid="trust-cta-pdf"
          >
            Download PDF <Download className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="section-y border-t border-white/[0.05]" data-testid="trust-principles">
        <div className="container-x">
          <SectionHeading
            eyebrow="Four commitments"
            title="How we hold your information."
            description="The same standards we apply to client financial data. No special pleading for marketing forms."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.05]">
            {PRINCIPLES.map((p) => (
              <div key={p.title} className="bg-ink p-8 md:p-10">
                <div className="flex items-center justify-between">
                  <span className="overline">{p.title}</span>
                  <p.icon className="w-5 h-5 text-gold" />
                </div>
                <p className="mt-6 text-zinc-400 leading-relaxed text-[15px]">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI STRATEGIST DISCLOSURE */}
      <section className="section-y bg-[#070707] border-y border-white/[0.05]" data-testid="trust-ai">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="overline flex items-center gap-2">
                  <Bot className="w-3.5 h-3.5" /> Voice + AI specific
                </div>
                <h2 className="mt-6 font-display font-light tracking-tight text-3xl md:text-4xl text-balance leading-[1.1]">
                  AI Strategist call disclosure.
                </h2>
                <p className="mt-6 text-zinc-400 leading-relaxed text-[15px]">
                  When you start a call with our AI Strategist, the
                  conversation is recorded, transcribed and analysed by our
                  third-party voice AI provider (Fireflies.ai). The transcript
                  is reviewed by a senior consultant who then prepares your
                  plan and responds within 1 to 2 business days.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={120}>
                <div className="border border-white/[0.06] bg-[#0a0a0a]">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/[0.05]">
                    {[
                      { k: "What is recorded", v: "Audio of your call + machine transcript." },
                      { k: "Who reads it", v: "Senior consultants at Toulios under written confidentiality." },
                      { k: "How long we keep it", v: "24 months, then permanently deleted." },
                      { k: "Your control", v: "Request deletion any time via hello@touliosadvisory.com." },
                    ].map((m) => (
                      <div key={m.k} className="bg-[#0a0a0a] p-6 md:p-8">
                        <div className="overline">{m.k}</div>
                        <div className="mt-3 text-sm text-zinc-200 leading-relaxed">{m.v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESSORS */}
      <section className="section-y" data-testid="trust-processors">
        <div className="container-x">
          <SectionHeading
            eyebrow="Sub-processors"
            title="Who else touches your data."
            description="The third parties we rely on. Each is independently audited and contractually bound to our privacy standards."
          />
          <div className="mt-12 border border-white/[0.06]">
            {PROCESSORS.map((p, i) => (
              <div
                key={p.name}
                className={`grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 p-6 md:p-8 ${
                  i !== 0 ? "border-t border-white/[0.06]" : ""
                }`}
              >
                <div className="md:col-span-3 flex items-center">
                  <Database className="w-4 h-4 text-gold mr-2" />
                  <span className="font-display text-lg">{p.name}</span>
                </div>
                <div className="md:col-span-3 text-sm text-zinc-300">{p.purpose}</div>
                <div className="md:col-span-3 text-sm text-zinc-400">{p.data}</div>
                <div className="md:col-span-2 text-xs text-zinc-500">{p.region}</div>
                <div className="md:col-span-1 text-right">
                  <a
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-gold hover:underline"
                  >
                    Policy <ArrowUpRight className="w-3 h-3 inline" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT FOR PRIVACY */}
      <section className="section-y border-t border-white/[0.05]" data-testid="trust-contact">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="overline flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Privacy enquiries
              </div>
              <h2 className="mt-6 font-display font-light tracking-tight text-3xl md:text-4xl text-balance">
                Want to access, correct, export, or delete your data?
              </h2>
              <p className="mt-6 text-zinc-400 leading-relaxed text-[15px] max-w-xl">
                Email <a href="mailto:privacy@touliosadvisory.com" className="text-gold hover:underline">privacy@touliosadvisory.com</a>{" "}
                with your request. We action within 30 days under the
                Australian Privacy Principles, usually within 5 business days.
                If you are unsatisfied with our response, you may contact the
                Office of the Australian Information Commissioner at oaic.gov.au.
              </p>
              <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
                Toulios Wealth Management Pty Ltd · ACN 667 133 977 · ABN 68 667 133 977 · Sydney, Australia.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-gold/30 bg-[#0a0a0a] p-6 md:p-8">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-gold" />
                  <span className="font-display text-lg">APP compliant</span>
                </div>
                <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
                  We collect, hold and use personal information consistent
                  with the Australian Privacy Principles set out in Schedule 1
                  of the <span className="text-zinc-300">Privacy Act 1988 (Cth)</span>.
                </p>
                <Link
                  to="/privacy"
                  className="mt-6 inline-flex items-center gap-1 text-sm text-gold hover:underline"
                  data-testid="trust-privacy-link"
                >
                  Read the full Privacy Policy <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
